import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';

const AgentProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const agentIdFromUrl = params.get('agent');
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const agentId = agentIdFromUrl || user.id;
  const isViewingOwnProfile = !agentIdFromUrl || agentIdFromUrl === String(user.id);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [agentData, setAgentData] = useState(null);
  const fileInputRef = useRef(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [checkingGoogle, setCheckingGoogle] = useState(true);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    phone_whatsapp: '',
    availability_time_from: '09:00',
    availability_time_to: '17:00',
    availability_days: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
    zoom_link: '',
    teams_link: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    twitter_url: '',
    youtube_url: '',
    public_slug: '',
    timezone: 'Africa/Cairo',
    profile_picture: '',
    language_skills: [],
    departments: [],
    modules: [],
    service_type: 'all',
    country: [],
    max_concurrent_conversations: 5,
    auto_assign: true,
    priority_score: 50
  });

  useEffect(() => {
    if (agentId) {
      fetchAgentData();
    }
  }, [agentId]);

useEffect(() => {
  if (agentId) {
    checkGoogleStatus();
  }
}, [agentId]);

useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const googleSuccess = params.get('google_success');
  const googleError = params.get('google_error');
  
  if (googleSuccess) {
    alert('\u2705 ' + googleSuccess);
    checkGoogleStatus();
    window.history.replaceState({}, '', '/agent-profile');
  }
  
  if (googleError) {
    alert('\u274C ' + googleError);
    window.history.replaceState({}, '', '/agent-profile');
  }
}, []);



const checkGoogleStatus = async () => {
  setCheckingGoogle(true);
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/v1/google/status', {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      const data = await response.json();
      setGoogleConnected(data.connected);
    }
  } catch (error) {
    console.error('Error checking Google status:', error);
  } finally {
    setCheckingGoogle(false);
  }
};

const handleGoogleConnect = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/v1/google/connect', {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } else {
      alert('\u274C Failed to connect to Google Calendar');
    }
  } catch (error) {
    console.error('Error:', error);
    alert('\u274C An error occurred');
  }
};

const handleGoogleDisconnect = async () => {
  if (!window.confirm('Do you want to disconnect Google Calendar?')) return;
  
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/v1/google/disconnect', {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      alert('\u2705 Google Calendar disconnected successfully');
      setGoogleConnected(false);
    } else {
      alert('\u274C Failed to disconnect Google Calendar');
    }
  } catch (error) {
    console.error('Error:', error);
    alert('\u274C An error occurred');
  }
};




  const fetchAgentData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/agents/${agentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const agent = data.data;
          setAgentData(agent);
          
          const parseField = (field) => {
            if (!field) return [];
            if (typeof field === 'string') {
              try { return JSON.parse(field); } catch { return []; }
            }
            return Array.isArray(field) ? field : [];
          };
          
          setFormData({
            full_name: agent.full_name || '',
            email: agent.email || '',
            phone: agent.phone || '',
            phone_whatsapp: agent.phone_whatsapp || '',
            availability_time_from: agent.availability_time_from || '09:00',
            availability_time_to: agent.availability_time_to || '17:00',
            availability_days: parseField(agent.availability_days).length > 0 
              ? parseField(agent.availability_days)
              : ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
            zoom_link: agent.zoom_link || '',
            teams_link: agent.teams_link || '',
            facebook_url: agent.facebook_url || '',
            instagram_url: agent.instagram_url || '',
            linkedin_url: agent.linkedin_url || '',
            twitter_url: agent.twitter_url || '',
            youtube_url: agent.youtube_url || '',
            public_slug: agent.public_slug || '',
            timezone: agent.timezone || 'Africa/Cairo',
            profile_picture: agent.profile_picture || '',
            language_skills: parseField(agent.language_skills),
            departments: parseField(agent.departments),
            modules: parseField(agent.modules),
            service_type: agent.service_type || 'all',
            country: parseField(agent.country),
            max_concurrent_conversations: agent.max_concurrent_conversations || 5,
            auto_assign: agent.auto_assign !== undefined ? agent.auto_assign : true,
            priority_score: agent.priority_score || 50
          });
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('\u274C Unsupported file type. Use: JPG, PNG, GIF, WEBP');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('\u274C File too large. Maximum: 2MB');
      return;
    }

    setUploading(true);

    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('profile_picture', file);
      formDataUpload.append('agent_id', agentId);

      const response = await fetch('/api/v1/agents/upload-picture', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('\u2705 Photo uploaded successfully!');
          setFormData(prev => ({
            ...prev,
            profile_picture: data.file_url
          }));
          fetchAgentData();
        } else {
          alert('\u274C Failed to upload photo: ' + (data.message || 'Unknown error'));
        }
      } else {
        alert('\u274C Failed to upload photo');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('\u274C An error occurred while uploading');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/agents/${agentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        alert('\u2705 Updated successfully!');
        fetchAgentData();
      } else {
        alert('\u274C An error occurred while saving');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('\u274C An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const timezones = [
    'Africa/Cairo', 'Asia/Riyadh', 'Asia/Kuwait', 'Asia/Dubai',
    'Europe/London', 'America/New_York', 'Asia/Tokyo'
  ];

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>{'\u23F0'}</div>
          <div>Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!agentData) {
    return (
      <Layout>
        <div style={{ padding: '60px', textAlign: 'center' }}>
          <h2>Agent not found</h2>
          <button onClick={() => navigate('/agents')}>Go Back</button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '25px',
          borderRadius: '12px',
          marginBottom: '25px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h1 style={{ margin: 0, fontSize: '24px' }}>
              {isViewingOwnProfile ? '\uD83D\uDC64 My Profile' : `\uD83D\uDC64 Profile: ${agentData.full_name}`}
            </h1>
            {!isViewingOwnProfile && (
              <button
                onClick={() => navigate('/agents')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}>
                {'\u2190'} Back
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{
              width: '80px', 
              height: '80px', 
              borderRadius: '50%',
              backgroundColor: formData.profile_picture ? 'transparent' : 'rgba(255,255,255,0.3)',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '36px', 
              fontWeight: 'bold',
              border: '3px solid white',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {formData.profile_picture ? (
                <img 
                  src={formData.profile_picture} 
                  alt="Profile" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }}
                />
              ) : (
                agentData.full_name.charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>{agentData.full_name}</div>
              <div style={{ opacity: 0.9 }}>{agentData.email}</div>
              <div style={{ opacity: 0.9 }}>{agentData.phone}</div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          
          {/* Profile Picture Upload */}
          <div style={{
            backgroundColor: 'rgba(26, 32, 56, 0.8)',
            padding: '25px',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              {'\uD83D\uDCF8'} Profile Picture
            </h3>
            
            <div style={{
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              margin: '0 auto 20px',
              backgroundColor: formData.profile_picture ? 'transparent' : '#667eea',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '64px',
              color: 'white',
              fontWeight: 'bold',
              border: '4px solid rgba(255,255,255,0.2)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {formData.profile_picture ? (
                <img 
                  src={formData.profile_picture} 
                  alt="Profile" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }}
                />
              ) : (
                agentData.full_name.charAt(0).toUpperCase()
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: uploading ? '#6c757d' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                {uploading ? '\u23F3 Uploading...' : '\uD83D\uDCCE Choose Photo'}
              </button>

              {formData.profile_picture && (
  <button
    type="button"
    onClick={async () => {
      if (window.confirm('Do you want to delete the photo?')) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/v1/agents/${agentId}/profile-picture`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();

          if (response.ok && data.success) {
            alert('\u2705 Photo deleted successfully!');
            setFormData(prev => ({ ...prev, profile_picture: '' }));
            fetchAgentData();
          } else {
            alert('\u274C Failed to delete photo: ' + (data.message || 'Error'));
          }
        } catch (error) {
          console.error('Delete error:', error);
          alert('\u274C An error occurred while deleting the photo');
        }
      }
    }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                  {'\uD83D\uDDD1\uFE0F'} Delete Photo
                </button>
              )}
            </div>

            <p style={{ margin: '15px 0 0 0', fontSize: '13px', color: '#9ca3af' }}>
              Supported: JPG, PNG, GIF, WEBP {'\u2022'} Max size: 2MB
            </p>
          </div>

          {/* General Information */}
          <div style={{
            backgroundColor: 'rgba(26, 32, 56, 0.8)',
            padding: '25px', borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              {'\uD83D\uDCCB'} Basic Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>Full Name</label>
                <input type="text" value={formData.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>Email</label>
                <input type="email" value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>Phone</label>
                <input type="tel" value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>WhatsApp</label>
                <input type="tel" value={formData.phone_whatsapp}
                  onChange={(e) => handleChange('phone_whatsapp', e.target.value)}
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
            </div>
          </div>

          {/* Meeting Links */}
          <div style={{
            backgroundColor: 'rgba(26, 32, 56, 0.8)',
            padding: '25px', borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              {'\uD83C\uDFA5'} Meeting Links
            </h3>
            <div style={{ display: 'grid', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>Zoom Link</label>
                <input type="url" value={formData.zoom_link}
                  onChange={(e) => handleChange('zoom_link', e.target.value)}
                  placeholder="https://us06web.zoom.us/j/..."
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>Microsoft Teams Link</label>
                <input type="url" value={formData.teams_link}
                  onChange={(e) => handleChange('teams_link', e.target.value)}
                  placeholder="https://teams.microsoft.com/..."
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div style={{
            backgroundColor: 'rgba(26, 32, 56, 0.8)',
            padding: '25px', borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              {'\uD83D\uDCF1'} Social Media
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>Facebook</label>
                <input type="url" value={formData.facebook_url}
                  onChange={(e) => handleChange('facebook_url', e.target.value)}
                  placeholder="https://facebook.com/username"
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>LinkedIn</label>
                <input type="url" value={formData.linkedin_url}
                  onChange={(e) => handleChange('linkedin_url', e.target.value)}
                  placeholder="https://linkedin.com/in/username"
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>Instagram</label>
                <input type="url" value={formData.instagram_url}
                  onChange={(e) => handleChange('instagram_url', e.target.value)}
                  placeholder="https://instagram.com/username"
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>Twitter (X)</label>
                <input type="url" value={formData.twitter_url}
                  onChange={(e) => handleChange('twitter_url', e.target.value)}
                  placeholder="https://x.com/username"
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>YouTube</label>
                <input type="url" value={formData.youtube_url}
                  onChange={(e) => handleChange('youtube_url', e.target.value)}
                  placeholder="https://youtube.com/@channel"
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#d1d5db' }}>Public Slug</label>
                <input type="text" value={formData.public_slug}
                  onChange={(e) => handleChange('public_slug', e.target.value)}
                  placeholder="mohamed-elsebaey"
                  style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }} />
                {formData.public_slug && (
                  <small style={{ color: '#9ca3af', marginTop: '5px', display: 'block' }}>
                    {'\uD83D\uDD17'} Your link: https://agents.matix.one/book/{formData.public_slug}
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Timezone */}
          <div style={{
            backgroundColor: 'rgba(26, 32, 56, 0.8)',
            padding: '25px', borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
              {'\uD83C\uDF10'} Timezone
            </h3>
            <select value={formData.timezone}
              onChange={(e) => handleChange('timezone', e.target.value)}
              style={{ width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
                borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb' }}>
              {timezones.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

        {/* Google Calendar Integration */}
<div style={{
  backgroundColor: 'rgba(26, 32, 56, 0.8)',
  padding: '25px',
  borderRadius: '12px',
  marginBottom: '20px',
  border: '1px solid rgba(255, 255, 255, 0.1)'
}}>
  <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#f9fafb' }}>
    {'\uD83D\uDCC5'} Google Calendar Integration
  </h3>
  
  {checkingGoogle ? (
    <div style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>
      {'\u23F3'} Checking connection...
    </div>
  ) : googleConnected ? (
    <div>
      <div style={{
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        border: '2px solid rgba(40, 167, 69, 0.3)',
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{ fontSize: '24px' }}>{'\u2705'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#28a745', fontWeight: '600', marginBottom: '5px' }}>
            Connected to Google Calendar
          </div>
          <div style={{ color: '#9ca3af', fontSize: '13px' }}>
            Your bookings will automatically sync with Google Calendar and create Google Meet links
          </div>
        </div>
      </div>
      
      <button
        type="button"
        onClick={handleGoogleDisconnect}
        style={{
          padding: '12px 24px',
          backgroundColor: '#dc3545',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600'
        }}>
        {'\uD83D\uDD0C'} Disconnect Google Calendar
      </button>
    </div>
  ) : (
    <div>
      <div style={{
        backgroundColor: 'rgba(220, 53, 69, 0.1)',
        border: '2px solid rgba(220, 53, 69, 0.3)',
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{ fontSize: '24px' }}>{'\u26A0\uFE0F'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#dc3545', fontWeight: '600', marginBottom: '5px' }}>
            Not Connected
          </div>
          <div style={{ color: '#9ca3af', fontSize: '13px' }}>
            Connect your Google Calendar to automatically sync bookings and create Google Meet links
          </div>
        </div>
      </div>
      
      <button
        type="button"
        onClick={handleGoogleConnect}
        style={{
          padding: '12px 24px',
          backgroundColor: '#4285F4',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
        <span style={{ fontSize: '18px' }}>{'\uD83D\uDD17'}</span>
        <span>Connect Google Calendar</span>
      </button>
    </div>
  )}
</div>

          {/* Save Button */}
          <button type="submit" disabled={saving}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: saving ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '17px',
              fontWeight: '700',
              boxShadow: '0 4px 14px rgba(0, 123, 255, 0.4)'
            }}>
            {saving ? '\u23F3 Saving...' : '\uD83D\uDCBE Save Changes'}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default AgentProfile;